/**
 * @fileoverview アプリケーションの「状態(State)」を一元管理します。
 * 現在のクイズ進行状況、ユーザーの選択、キャッシュデータなど、
 * アプリケーション全体で共有される動的なデータを保持・操作します。
 */

import { Constants } from "./constants";
import type { Question } from "./types/question.d";
import { AppState } from "./types/state.d";

const State: AppState = {
  // AppState型を適用
  /**
   * @property {User | null} currentUser - 現在ログインしているユーザーのセッション情報。
   *           未ログインの場合はnull。
   */
  currentUser: null,

  /**
   * @property {string | null} sessionId - 現在のクイズセッションID。
   *           各クイズセッションを一意に識別するために使用されます。
   */
  sessionId: null,

  /**
   * @property {Question[]} currentQuestions - 現在のクイズで表示されている問題の配列。
   */
  currentQuestions: [],

  /**
   * @property {number} currentQuestionIndex - 現在表示している問題のインデックス。
   */
  currentQuestionIndex: 0,

  /**
   * @property {number} score - 現在のクイズの正解数。
   */
  score: 0,

  /**
   * @property {number} selectedQuizLength - ユーザーが選択した問題数。
   */
  selectedQuizLength: Constants.APP_CONFIG.QUIZ_LENGTH_OPTIONS[0], // デフォルトは最初の選択肢

  /**
   * @property {string} selectedQuizAlgorithm - ユーザーが選択した問題抽出アルゴリズム。
   */
  selectedQuizAlgorithm: Constants.APP_CONFIG.DEFAULT_QUIZ_ALGORITHM, // デフォルトはバランスモード

  /**
   * @property {string} selectedQuizMode - ユーザーが選択した出題モード。
   */
  selectedQuizMode: Constants.APP_CONFIG.DEFAULT_QUIZ_MODE, // デフォルトはアダプティブモード

  /**
   * @property {number[]} selectedAreaIds - ユーザーが選択した試験領域のID配列。
   */
  selectedAreaIds: [], // 初期値は空の配列

  /**
   * @property {string | null} currentQuestionDisplayMode - 現在の問題で実際に使用された表示モード。
   */
  currentQuestionDisplayMode: null,

  /**
   * @property {{ [key: number]: Question[] }} prefetchedQuestions - 試験IDをキーとして、プリフェッチされた問題データを保持するキャッシュ。
   */
  prefetchedQuestions: {},

  /**
   * @property {{ [key: number]: { total: number; correct: number } }} areaStats - 領域ごとの正解数と問題数を保持する統計情報。
   *           キーは領域ID、値は { total: number, correct: number }。
   */
  areaStats: {},

  /**
   * @property {ExamData | null} currentExamData - 現在選択されている試験のデータ。
   */
  currentExamData: null,

  /**
   * @property {Chart | null} resultChartInstance - 総合結果ドーナツチャートのChart.jsインスタンス。
   */
  resultChartInstance: null,

  /**
   * @property {Chart | null} areaResultChartInstance - 領域別正解率レーダーチャートのChart.jsインスタンス。
   */
  areaResultChartInstance: null,

  /**
   * @property {number | null} questionStartTime - 現在の問題が表示された時刻（Date.now()の値）。
   */
  questionStartTime: null,

  /**
   * @property {number | null} recallPhaseEndTime - フラッシュカードモードで選択肢が表示された時刻（Date.now()の値）。
   */
  recallPhaseEndTime: null,

  /**
   * @property {(() => void) | null} _currentDialogOkCallback - 現在表示中のカスタムダイアログのOKボタンコールバック。
   */
  _currentDialogOkCallback: null,

  /**
   * @property {(() => void) | null} _currentDialogCancelCallback - 現在表示中のカスタムダイアログのキャンセルボタンコールバック。
   */
  _currentDialogCancelCallback: null,

  /**
   * @property {Option[]} shuffledOptionDetails - 現在の問題の選択肢をシャッフルした配列。
   */
  shuffledOptionDetails: [],

  /**
   * @property {HTMLElement | null} activeAccordionPanel - 現在開いているアコーディオンパネルのHTMLElement。
   */
  activeAccordionPanel: null,

  /**
   * @property {string | null} editingQuestionId - 現在編集中の問題ID。
   */
  editingQuestionId: null,

  /**
   * @property {boolean} hasEditPermission - 現在のユーザーが編集権限を持っているかどうか。
   */
  hasEditPermission: false,
  currentExamAreas: [],
  lastAnswerResult: null,

  /**
   * クイズの状態を初期値にリセットします。
   */
  resetQuizState: function (): void {
    this.currentQuestions = [];
    this.currentQuestionIndex = 0;
    this.score = 0;
    this.areaStats = {};
    this.currentExamData = null;
    this.questionStartTime = null;
    this.recallPhaseEndTime = null;
    this.shuffledOptionDetails = [];
    this.currentQuestionDisplayMode = null;
    this.selectedAreaIds = []; // リセット
    if (this.resultChartInstance) {
      this.resultChartInstance.destroy();
      this.resultChartInstance = null;
    }
    if (this.areaResultChartInstance) {
      this.areaResultChartInstance.destroy();
      this.areaResultChartInstance = null;
    }
    this.activeAccordionPanel = null;
  },

  /**
   * 現在の問題データを取得します。
   * @returns {Question | null} 現在の問題データ、または問題がなければnull。
   */
  getCurrentQuestion: function (): Question | null {
    if (
      this.currentQuestionIndex >= 0 &&
      this.currentQuestionIndex < this.currentQuestions.length
    ) {
      return this.currentQuestions[this.currentQuestionIndex];
    }
    return null;
  },

  /**
   * 正解数を1増やします。
   */
  incrementScore: function (): void {
    this.score++;
  },

  /**
   * 現在の問題インデックスを1増やし、次の問題へ進みます。
   */
  incrementQuestionIndex: function (): void {
    this.currentQuestionIndex++;
  },

  /**
   * 現在の問題が最後の問題かどうかを判定します。
   * @returns {boolean} 最後の問題であればtrue、そうでなければfalse。
   */
  isLastQuestion: function (): boolean {
    return this.currentQuestionIndex >= this.currentQuestions.length - 1;
  },

  /**
   * 配列をシャッフルします（Fisher-Yatesアルゴリズム）。
   * @template T
   * @param {T[]} array - シャッフルする配列。
   * @returns {T[]} シャッフルされた新しい配列。
   */
  shuffleArray: function <T>(array: T[]): T[] {
    const newArray = [...array]; // 元の配列を変更しないようにコピーを作成
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  },
};

export default State;
